import { useState, useEffect } from "react";
import { btnPrimary, btnSecondary, inputStyle, labelStyle } from "./shared";
import { getPetPolicyText, getLateFeeText, getMaintenanceText, getGuestText, getNoSmokingText } from "./leaseClauses";

const STEPS = ["Property", "Tenant", "Lease Terms", "Clauses", "Preview & Print"];

function ordinal(n) {
  const num = parseInt(n) || 1;
  if (num === 11 || num === 12 || num === 13) return "th";
  if (num % 10 === 1) return "st";
  if (num % 10 === 2) return "nd";
  if (num % 10 === 3) return "rd";
  return "th";
}

// company_profiles.company_name first, falling back to the account's own
// landlordName (already seeded from the auth user's display name at signup),
// so there's always something better than a blank line.
function resolveLandlordName(companyProfile, settings) {
  return companyProfile?.company_name || settings?.landlordName || "Landlord";
}

function buildLeaseText(d, settings, companyProfile) {
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const landlordName = resolveLandlordName(companyProfile, settings);

  const fmt = (iso) => iso
    ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  const startFmt = fmt(d.startDate) || "[Start Date]";
  const endFmt   = fmt(d.endDate)   || "[End Date]";

  const leaseTerm = d.monthToMonth
    ? `This Agreement shall commence on ${startFmt} and shall continue on a month-to-month basis until terminated by either party with thirty (30) days' written notice.`
    : `This Agreement shall commence on ${startFmt} and terminate on ${endFmt}, unless extended or earlier terminated pursuant to the terms herein.`;

  const dueOrd  = `${d.dueDay || 1}${ordinal(d.dueDay)}`;
  const rent    = d.monthlyRent  ? `$${Number(d.monthlyRent).toLocaleString()}`  : "[Monthly Rent]";
  const deposit = d.securityDeposit ? `$${Number(d.securityDeposit).toLocaleString()}` : "[Security Deposit]";

  const div = "\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n";

  let clauseNum = 4;
  let clauseText = "";

  clauseText += `\n${clauseNum++}. PET POLICY\n${getPetPolicyText(d.petPolicy, d.petDepositAmount)}\n`;

  const lateFeeText = getLateFeeText(d.lateFeePolicy, d.lateFeeType, d.lateFeeAmount);
  if (lateFeeText) {
    clauseText += `\n${clauseNum++}. LATE FEES\n${lateFeeText}\n`;
  }

  if (d.maintenancePolicy) {
    clauseText += `\n${clauseNum++}. MAINTENANCE & REPAIRS\n${getMaintenanceText()}\n`;
  }

  if (d.guestPolicy) {
    clauseText += `\n${clauseNum++}. GUEST POLICY\n${getGuestText()}\n`;
  }

  if (d.noSmoking) {
    clauseText += `\n${clauseNum++}. NO SMOKING\n${getNoSmokingText()}\n`;
  }

  return `RESIDENTIAL LEASE AGREEMENT
${div}
LANDLORD
Name:    ${landlordName}
Address: ${d.propertyAddress || "[Property Address]"}

TENANT
Name:    ${d.tenantName || "[Tenant Name]"}${d.tenantPhone ? "\nPhone:   " + d.tenantPhone : ""}${d.tenantEmail ? "\nEmail:   " + d.tenantEmail : ""}

Agreement Date: ${today}
${div}
1. LEASE TERM
${leaseTerm}

2. RENT
Tenant agrees to pay ${rent} per month, due on the ${dueOrd} day of each calendar month. Rent shall be paid to Landlord at the address above or as otherwise directed in writing by Landlord.

3. SECURITY DEPOSIT
Prior to or upon move-in, Tenant shall pay a security deposit of ${deposit}. This deposit shall be held in accordance with California Civil Code §1950.5 and returned within twenty-one (21) days of Tenant vacating the premises, less any lawful deductions for unpaid rent, cleaning, or damage beyond normal wear and tear.
${clauseText}
${div}
SIGNATURES

By signing below, both parties acknowledge they have read, understood, and agree to all terms of this Agreement.

LANDLORD

Signature: _________________________________  Date: _______________

Printed Name: _________________________________


TENANT

Signature: _________________________________  Date: _______________

Printed Name: ${d.tenantName || "[Tenant Name]"}
${div}
Powered by Leasly · Gibbs Prodigy Group · Pittsburg, CA
This document was generated on ${today}. Consult a licensed attorney before execution.`;
}

function buildPrintHtml(d, settings, companyProfile) {
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const esc = (s) => String(s || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const landlordName = resolveLandlordName(companyProfile, settings);

  const fmt = (iso) => iso
    ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  const startFmt = fmt(d.startDate) || "[Start Date]";
  const endFmt   = fmt(d.endDate)   || "[End Date]";
  const leaseTerm = d.monthToMonth
    ? `This Agreement shall commence on ${startFmt} and shall continue on a month-to-month basis until terminated by either party with thirty (30) days&#x27; written notice.`
    : `This Agreement shall commence on ${startFmt} and terminate on ${endFmt}, unless extended or earlier terminated pursuant to the terms herein.`;

  const dueOrd  = `${d.dueDay || 1}${ordinal(d.dueDay)}`;
  const rent    = d.monthlyRent    ? `$${Number(d.monthlyRent).toLocaleString()}`    : "[Monthly Rent]";
  const deposit = d.securityDeposit ? `$${Number(d.securityDeposit).toLocaleString()}` : "[Security Deposit]";

  let clauseNum = 4;
  let clausesHtml = `
  <div class="section">
    <div class="sec-title">${clauseNum++}. Pet Policy</div>
    <div class="sec-body">${esc(getPetPolicyText(d.petPolicy, d.petDepositAmount))}</div>
  </div>`;

  const lateFeeText = getLateFeeText(d.lateFeePolicy, d.lateFeeType, d.lateFeeAmount);
  if (lateFeeText) {
    clausesHtml += `
  <div class="section">
    <div class="sec-title">${clauseNum++}. Late Fees</div>
    <div class="sec-body">${esc(lateFeeText)}</div>
  </div>`;
  }

  if (d.maintenancePolicy) {
    clausesHtml += `
  <div class="section">
    <div class="sec-title">${clauseNum++}. Maintenance &amp; Repairs</div>
    <div class="sec-body">${esc(getMaintenanceText())}</div>
  </div>`;
  }

  if (d.guestPolicy) {
    clausesHtml += `
  <div class="section">
    <div class="sec-title">${clauseNum++}. Guest Policy</div>
    <div class="sec-body">${esc(getGuestText())}</div>
  </div>`;
  }

  if (d.noSmoking) {
    clausesHtml += `
  <div class="section">
    <div class="sec-title">${clauseNum++}. No Smoking</div>
    <div class="sec-body">${esc(getNoSmokingText())}</div>
  </div>`;
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Lease Agreement &#x2014; ${esc(d.tenantName || "Tenant")} &#x2014; ${esc(d.propertyAddress || "Property")}</title>
  <style>
    @page {
      margin: 1in;
      @bottom-center {
        content: "Gibbs Prodigy Group  |  Powered by Leasly  |  Page " counter(page) " of " counter(pages);
        font-size: 9pt;
        color: #777;
        font-family: Georgia, serif;
      }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Georgia, "Times New Roman", serif; font-size: 12pt; line-height: 1.85; color: #000; background: #fff; }
    .doc-header { text-align: center; padding-bottom: 14pt; border-bottom: 2pt solid #000; margin-bottom: 22pt; }
    .company-name { font-size: 15pt; font-weight: bold; font-variant: small-caps; letter-spacing: 0.12em; margin-bottom: 4pt; }
    .doc-title { font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4pt; }
    .doc-date { font-size: 10pt; color: #444; }
    .parties { width: 100%; border-collapse: collapse; margin: 18pt 0; }
    .parties td { width: 50%; vertical-align: top; font-size: 11pt; line-height: 1.75; padding: 0; }
    .parties td.r { padding-left: 28pt; border-left: 1pt solid #bbb; }
    .party-head { display: block; font-weight: bold; text-transform: uppercase; font-size: 10pt; letter-spacing: 0.08em; border-bottom: 1pt solid #000; padding-bottom: 4pt; margin-bottom: 8pt; }
    .fill-blank { display: inline-block; border-bottom: 1pt solid #000; width: 190pt; }
    .divider { border: none; border-top: 1pt solid #000; margin: 14pt 0; }
    .section { margin: 13pt 0; }
    .sec-title { font-weight: bold; text-transform: uppercase; font-size: 11pt; letter-spacing: 0.02em; margin-bottom: 5pt; }
    .sec-body { font-size: 11pt; line-height: 1.85; text-align: justify; }
    .sig-block { margin-top: 30pt; page-break-inside: avoid; }
    .sig-title { font-weight: bold; text-transform: uppercase; font-size: 11pt; letter-spacing: 0.08em; border-bottom: 1pt solid #000; padding-bottom: 6pt; margin-bottom: 18pt; }
    .sig-intro { font-size: 11pt; margin-bottom: 20pt; line-height: 1.7; }
    .sig-cols { display: flex; gap: 44pt; }
    .sig-col { flex: 1; }
    .sig-party { font-weight: bold; text-transform: uppercase; font-size: 10pt; letter-spacing: 0.06em; margin-bottom: 16pt; }
    .sig-line { border-bottom: 1pt solid #000; height: 26pt; margin-bottom: 3pt; }
    .sig-label { font-size: 9pt; color: #555; margin-bottom: 13pt; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>

  <div class="doc-header">
    <div class="company-name">Gibbs Prodigy Group</div>
    <div class="doc-title">Residential Lease Agreement</div>
    <div class="doc-date">${today}</div>
  </div>

  <table class="parties"><tr>
    <td>
      <span class="party-head">Landlord</span>
      <div>Name:&nbsp;&nbsp;&nbsp;${esc(landlordName)}</div>
      <div>Address: ${esc(d.propertyAddress || "[Property Address]")}</div>
    </td>
    <td class="r">
      <span class="party-head">Tenant</span>
      <div>Name:&nbsp;&nbsp;&nbsp;${esc(d.tenantName || "[Tenant Name]")}</div>
      ${d.tenantPhone ? `<div>Phone:&nbsp;&nbsp;${esc(d.tenantPhone)}</div>` : ""}
      ${d.tenantEmail ? `<div>Email:&nbsp;&nbsp;&nbsp;${esc(d.tenantEmail)}</div>` : ""}
    </td>
  </tr></table>

  <hr class="divider" />

  <div class="section">
    <div class="sec-title">1. Lease Term</div>
    <div class="sec-body">${leaseTerm}</div>
  </div>

  <div class="section">
    <div class="sec-title">2. Rent</div>
    <div class="sec-body">Tenant agrees to pay ${rent} per month, due on the ${dueOrd} day of each calendar month. Rent shall be paid to Landlord at the address above or as otherwise directed in writing by Landlord.</div>
  </div>

  <div class="section">
    <div class="sec-title">3. Security Deposit</div>
    <div class="sec-body">Prior to or upon move-in, Tenant shall pay a security deposit of ${deposit}. This deposit shall be held in accordance with California Civil Code &#xA7;1950.5 and returned within twenty-one (21) days of Tenant vacating the premises, less any lawful deductions for unpaid rent, cleaning, or damage beyond normal wear and tear.</div>
  </div>

  ${clausesHtml}

  <div class="sig-block">
    <div class="sig-title">Signatures</div>
    <p class="sig-intro">By signing below, both parties acknowledge they have read, understood, and agree to all terms of this Agreement.</p>
    <div class="sig-cols">
      <div class="sig-col">
        <div class="sig-party">Landlord</div>
        <div class="sig-line"></div><div class="sig-label">Signature</div>
        <div class="sig-line"></div><div class="sig-label">Printed Name</div>
        <div class="sig-line"></div><div class="sig-label">Date</div>
      </div>
      <div class="sig-col">
        <div class="sig-party">Tenant</div>
        <div class="sig-line"></div><div class="sig-label">Signature</div>
        <div class="sig-line"></div><div class="sig-label">Printed Name: ${esc(d.tenantName || "")}</div>
        <div class="sig-line"></div><div class="sig-label">Date</div>
      </div>
    </div>
  </div>

  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;
}

const radioStyle = { display: "flex", gap: 20, marginTop: 6 };
const radioBtn = (active) => ({
  display: "flex", alignItems: "center", gap: 7, cursor: "pointer",
  fontSize: 13, color: active ? "#163C2F" : "#9B9690", fontWeight: active ? 600 : 400,
});

const toggleRow = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "12px 16px", background: "#F5F1EA", borderRadius: 10,
  border: "1px solid #e0dbd0",
};

// Live "preview text" toggle + resolved paragraph, shared by every clause row
// in Step 4 — reads the same helper functions that build the final document,
// so what you see here is exactly what will end up in the lease.
function ClausePreviewToggle({ open, onToggle, text }) {
  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={onToggle}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "#214D3A", fontFamily: "'Manrope', sans-serif", fontWeight: 600, textDecoration: "underline" }}
      >
        {open ? "▲ Hide full text" : "👁 Preview full text"}
      </button>
      {open && (
        <div style={{ marginTop: 8, padding: "10px 12px", background: "#FFFFFF", border: "1px solid #e0dbd0", borderRadius: 8, fontSize: 12, color: "#2d4a2d", lineHeight: 1.6, fontFamily: "Georgia, 'Times New Roman', serif" }}>
          {text}
        </div>
      )}
    </div>
  );
}

export default function LeaseWizard({
  tenants, properties, settings, companyProfile, onClose,
  submitLease, leaseActionPending, leaseActionError, setLeaseActionError,
  setPage, setPropTab, checkLeaseConflict,
}) {
  const [step, setStep]   = useState(1);
  const [finalized, setFinalized] = useState(null); // { action: "draft"|"send" } once submitLease succeeds
  const [sendValidationErr, setSendValidationErr] = useState("");
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [previewOpen, setPreviewOpen] = useState({}); // { pet: bool, lateFee: bool, maintenance: bool, guest: bool, noSmoking: bool }
  const [data, setData]   = useState({
    propertyMode:      "existing",
    propertyId:        "",
    propertyAddress:   "",
    unit:              "",
    addrStreet:        "",
    addrCity:          "",
    addrState:         "CA",
    addrZip:           "",
    tenantMode:        "existing",
    tenantId:          "",
    tenantName:        "",
    tenantEmail:       "",
    tenantPhone:       "",
    startDate:         "",
    endDate:           "",
    monthToMonth:      false,
    monthlyRent:       "",
    dueDay:            "1",
    securityDeposit:   "",
    petPolicy:         "no_pets",
    petDepositAmount:  "",
    lateFeePolicy:     true,
    lateFeeType:       "percentage",
    lateFeeAmount:     "",
    maintenancePolicy: true,
    guestPolicy:       true,
    noSmoking:         true,
  });

  // leaseActionError lives in the composer (useLeasesData), not as local state
  // here — it survives across the wizard closing/reopening. Clear it on mount
  // so a stale error from a previous session doesn't render before anything's
  // actually been checked this time.
  useEffect(() => {
    setLeaseActionError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setData(s => ({ ...s, [k]: v }));
  const togglePreview = (key) => setPreviewOpen(s => ({ ...s, [key]: !s[key] }));

  const leaseText = buildLeaseText(data, settings, companyProfile);

  const selectedProperty = data.propertyMode === "existing"
    ? (properties || []).find(p => String(p.id) === data.propertyId)
    : null;
  const requiresUnit = !!selectedProperty && Number(selectedProperty.units) > 1;

  const canNext = () => {
    if (step === 1) {
      const hasProperty = data.propertyMode === "existing" ? !!data.propertyId : !!data.addrStreet.trim();
      if (!hasProperty) return false;
      if (requiresUnit && !data.unit.trim()) return false;
      return true;
    }
    if (step === 2) return !!(data.tenantMode === "existing" ? data.tenantId : data.tenantName.trim());
    if (step === 3) return !!(data.startDate && data.monthlyRent && (data.monthToMonth || data.endDate));
    return true;
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) { alert("Pop-up blocked — please allow pop-ups for this site."); return; }
    w.document.write(buildPrintHtml(data, settings, companyProfile));
    w.document.close();
  };

  const buildLeasePayload = (action) => ({
    action,
    leaseText: leaseText,
    terms: data,
    property: {
      mode:    data.propertyMode,
      propertyId: data.propertyId || null,
      address: data.propertyAddress,
      unit:    data.unit || null,
      city:    data.addrCity,
      state:   data.addrState,
      zip:     data.addrZip,
    },
    tenant: {
      mode:   data.tenantMode,
      tenantId: data.tenantId || null,
      name:   data.tenantName,
      email:  data.tenantEmail,
      phone:  data.tenantPhone,
    },
    tenantEmail: data.tenantEmail,
  });

  const handleSaveDraft = async () => {
    setLeaseActionError(null);
    const result = await submitLease(buildLeasePayload("draft"));
    if (result.success) setFinalized({ action: "draft" });
  };

  const handleSendForSignature = async () => {
    setLeaseActionError(null);
    if (!data.tenantEmail || !data.tenantEmail.trim()) {
      setSendValidationErr("A tenant email address is required to send a lease for signature.");
      return;
    }
    setSendValidationErr("");
    const result = await submitLease(buildLeasePayload("send"));
    if (result.success) setFinalized({ action: "send" });
  };

  const clearStepErrors = () => {
    setLeaseActionError(null);
    setSendValidationErr("");
  };

  const handleNext = async () => {
    clearStepErrors();
    if (step === 3) {
      const { property, tenant, terms } = buildLeasePayload("draft");
      setCheckingConflict(true);
      const result = await checkLeaseConflict({ property, tenant, terms });
      setCheckingConflict(false);
      if (result.hasConflict) { setLeaseActionError(result.message); return; }
    }
    setStep(s => s + 1);
  };

  const handleBack = () => {
    clearStepErrors();
    setStep(s => s - 1);
  };

  const goToStep = (s) => {
    clearStepErrors();
    setStep(s);
  };

  if (finalized) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: 16, padding: 36, width: 440, maxWidth: "94vw", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>✅</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 20, color: "#163C2F", marginBottom: 10 }}>
            {finalized.action === "send" ? "Lease sent for signature!" : "Lease saved!"}
          </div>
          <div style={{ fontSize: 13, color: "#9B9690", marginBottom: 24, lineHeight: 1.6 }}>
            {finalized.action === "send"
              ? `An email with a secure signing link was sent to ${data.tenantEmail}. You'll be notified once it's signed.`
              : "This lease has been saved to Properties → Leases. You can send it for signature anytime."}
          </div>
          <button
            onClick={() => { onClose(); setPage("Properties"); setPropTab("leases"); }}
            style={btnPrimary}
          >
            View in Properties → Leases
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="app-modal-inner" style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: 16, padding: 0, width: 680, maxWidth: "96vw", maxHeight: "94vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "20px 28px 0", borderBottom: "1px solid #e0dbd0", paddingBottom: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 18, color: "#163C2F" }}>📜 Create Lease Agreement</div>
              <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>Step {step} of {STEPS.length} — {STEPS[step - 1]}</div>
            </div>
            <button onClick={onClose} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 16 }}>✕</button>
          </div>
          {/* Step bar */}
          <div style={{ display: "flex", gap: 0, marginBottom: -1 }}>
            {STEPS.map((label, i) => {
              const s = i + 1;
              const active = s === step;
              const done   = s < step;
              return (
                <button key={s}
                  onClick={() => s < step && goToStep(s)}
                  style={{
                    flex: 1, padding: "8px 4px", border: "none", cursor: s < step ? "pointer" : "default",
                    background: "transparent", fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    color: active ? "#163C2F" : done ? "#22c55e" : "#9ca3af",
                    borderBottom: active ? "2.5px solid #163C2F" : done ? "2.5px solid #22c55e" : "2.5px solid transparent",
                    fontFamily: "'Manrope', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                  {done ? "✓ " : ""}{label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>

          {/* ── Step 1: Property ── */}
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Property Source</label>
                <div style={radioStyle}>
                  {[["existing","Choose from existing"], ["manual","Enter address manually"]].map(([val, lbl]) => (
                    <label key={val} style={radioBtn(data.propertyMode === val)}>
                      <input type="radio" name="propMode" value={val} checked={data.propertyMode === val}
                        onChange={() => set("propertyMode", val)} style={{ accentColor: "#163C2F" }} />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>
              {data.propertyMode === "existing" ? (
                <div>
                  <label style={labelStyle}>Select Property</label>
                  <select value={data.propertyId} onChange={e => {
                    const p = (properties || []).find(x => String(x.id) === e.target.value);
                    set("propertyId", e.target.value);
                    set("propertyAddress", p ? p.address : "");
                  }} style={inputStyle}>
                    <option value="">— Select a property —</option>
                    {(properties || []).map(p => (
                      <option key={p.id} value={p.id}>{p.address}{p.units > 1 ? ` (${p.units} units)` : ""}</option>
                    ))}
                  </select>
                  {data.propertyAddress && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#9B9690", fontStyle: "italic" }}>📍 {data.propertyAddress}</div>
                  )}
                  {requiresUnit && (
                    <div style={{ marginTop: 12 }}>
                      <label style={labelStyle}>Unit / Suite <span style={{ color: "#dc2626" }}>*</span></label>
                      <input type="text" value={data.unit} onChange={e => set("unit", e.target.value)}
                        style={inputStyle} placeholder="e.g. Unit 2" />
                      <div style={{ fontSize: 11, color: "#9B9690", marginTop: 4 }}>
                        This property has {selectedProperty.units} units — specify which one this lease is for.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Street Address</label>
                    <input type="text" value={data.addrStreet}
                      onChange={e => setData(s => { const v = e.target.value; return { ...s, addrStreet: v, propertyAddress: [v, s.addrCity, s.addrState || "CA", s.addrZip].filter(Boolean).join(", ") }; })}
                      style={inputStyle} placeholder="e.g. 155 West Siino Ave" />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelStyle}>City</label>
                      <input type="text" value={data.addrCity}
                        onChange={e => setData(s => { const v = e.target.value; return { ...s, addrCity: v, propertyAddress: [s.addrStreet, v, s.addrState || "CA", s.addrZip].filter(Boolean).join(", ") }; })}
                        style={inputStyle} placeholder="e.g. Pittsburg" />
                    </div>
                    <div>
                      <label style={labelStyle}>State</label>
                      <input type="text" value={data.addrState}
                        onChange={e => setData(s => { const v = e.target.value; return { ...s, addrState: v, propertyAddress: [s.addrStreet, s.addrCity, v, s.addrZip].filter(Boolean).join(", ") }; })}
                        style={inputStyle} placeholder="CA" />
                    </div>
                    <div>
                      <label style={labelStyle}>Zip Code</label>
                      <input type="text" value={data.addrZip}
                        onChange={e => setData(s => { const v = e.target.value; return { ...s, addrZip: v, propertyAddress: [s.addrStreet, s.addrCity, s.addrState || "CA", v].filter(Boolean).join(", ") }; })}
                        style={inputStyle} placeholder="e.g. 94565" />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Unit / Suite (optional)</label>
                    <input type="text" value={data.unit} onChange={e => set("unit", e.target.value)}
                      style={inputStyle} placeholder="e.g. Unit 2 — leave blank if single-unit" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Tenant ── */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Tenant Source</label>
                <div style={radioStyle}>
                  {[["existing","Choose existing tenant"], ["manual","Enter manually"]].map(([val, lbl]) => (
                    <label key={val} style={radioBtn(data.tenantMode === val)}>
                      <input type="radio" name="tenantMode" value={val} checked={data.tenantMode === val}
                        onChange={() => set("tenantMode", val)} style={{ accentColor: "#163C2F" }} />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>
              {data.tenantMode === "existing" ? (
                <div>
                  <label style={labelStyle}>Select Tenant</label>
                  <select value={data.tenantId} onChange={e => {
                    const t = (tenants || []).find(x => String(x.id) === e.target.value || x.name === e.target.value);
                    set("tenantId", e.target.value);
                    if (t) {
                      set("tenantName",  t.name  || "");
                      set("tenantEmail", t.email || "");
                      set("tenantPhone", t.phone || "");
                      if (!data.unit && t.property) set("unit", t.property);
                      if (!data.monthlyRent && t.rent) set("monthlyRent", String(t.rent));
                      if (!data.startDate && t.lease_start) set("startDate", t.lease_start);
                      if (!data.endDate   && t.lease_end)   set("endDate",   t.lease_end);
                    }
                  }} style={inputStyle}>
                    <option value="">— Select a tenant —</option>
                    {(tenants || []).map(t => (
                      <option key={t.id || t.name} value={t.id || t.name}>{t.name}{t.address ? ` · ${t.address}` : ""}</option>
                    ))}
                  </select>
                  {data.tenantName && (
                    <div style={{ marginTop: 10, background: "#F5F1EA", borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
                      <div style={{ fontWeight: 600 }}>{data.tenantName}</div>
                      {data.tenantEmail && <div style={{ color: "#9B9690" }}>{data.tenantEmail}</div>}
                      {data.tenantPhone && <div style={{ color: "#9B9690" }}>{data.tenantPhone}</div>}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Full Name</label>
                    <input type="text" value={data.tenantName} onChange={e => set("tenantName", e.target.value)}
                      style={inputStyle} placeholder="e.g. Jordan Smith" />
                  </div>
                  <div>
                    <label style={labelStyle}>Email Address</label>
                    <input type="email" value={data.tenantEmail} onChange={e => set("tenantEmail", e.target.value)}
                      style={inputStyle} placeholder="tenant@email.com" />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone Number</label>
                    <input type="tel" value={data.tenantPhone} onChange={e => set("tenantPhone", e.target.value)}
                      style={inputStyle} placeholder="(555) 000-0000" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Lease Terms ── */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={labelStyle}>Lease Start Date</label>
                  <input type="date" value={data.startDate} onChange={e => set("startDate", e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Monthly Rent ($)</label>
                  <input type="number" min="0" value={data.monthlyRent} onChange={e => set("monthlyRent", e.target.value)}
                    style={inputStyle} placeholder="e.g. 1800" />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Lease End</label>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, marginBottom: 10 }}>
                  <label style={radioBtn(data.monthToMonth)}>
                    <input type="checkbox" checked={data.monthToMonth} onChange={e => set("monthToMonth", e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: "#163C2F", cursor: "pointer" }} />
                    Month-to-month (no fixed end date)
                  </label>
                </div>
                {!data.monthToMonth && (
                  <input type="date" value={data.endDate} onChange={e => set("endDate", e.target.value)} style={inputStyle} />
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={labelStyle}>Due Day of Month</label>
                  <input type="number" min="1" max="28" value={data.dueDay} onChange={e => set("dueDay", e.target.value)}
                    style={inputStyle} placeholder="1" />
                </div>
                <div>
                  <label style={labelStyle}>Security Deposit ($)</label>
                  <input type="number" min="0" value={data.securityDeposit} onChange={e => set("securityDeposit", e.target.value)}
                    style={inputStyle} placeholder="e.g. 2000" />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 4: Clauses ── */}
          {step === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ ...toggleRow, flexDirection: "column", alignItems: "stretch", gap: 0 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>🐾 Pet Policy</div>
                  <select value={data.petPolicy} onChange={e => set("petPolicy", e.target.value)}
                    style={{ ...inputStyle, maxWidth: 340, marginTop: 4 }}>
                    <option value="no_pets">No pets of any kind permitted</option>
                    <option value="cats_only">Cats only (with approval)</option>
                    <option value="dogs_deposit">Dogs allowed with deposit</option>
                    <option value="all_deposit">All pets allowed with deposit</option>
                  </select>
                  {(data.petPolicy === "dogs_deposit" || data.petPolicy === "all_deposit") && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <span style={{ fontSize: 13, color: "#9B9690" }}>$</span>
                      <input
                        type="number" min="0" value={data.petDepositAmount}
                        onChange={e => set("petDepositAmount", e.target.value)}
                        style={{ ...inputStyle, maxWidth: 140, marginBottom: 0 }}
                        placeholder="e.g. 300"
                      />
                      <span style={{ fontSize: 12, color: "#9B9690" }}>non-refundable deposit</span>
                    </div>
                  )}
                </div>
                <ClausePreviewToggle
                  open={!!previewOpen.pet} onToggle={() => togglePreview("pet")}
                  text={getPetPolicyText(data.petPolicy, data.petDepositAmount)}
                />
              </div>

              {/* Late Fee — custom row with sub-options */}
              <div style={{ ...toggleRow, flexDirection: "column", alignItems: "stretch", gap: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>💰 Late Fee Policy</div>
                    {!data.lateFeePolicy && (
                      <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>Late fee after a 5-day grace period</div>
                    )}
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <div style={{
                      width: 44, height: 24, borderRadius: 12, cursor: "pointer",
                      background: data.lateFeePolicy ? "#163C2F" : "#d1d5db",
                      position: "relative", transition: "background 0.2s", flexShrink: 0,
                    }} onClick={() => set("lateFeePolicy", !data.lateFeePolicy)}>
                      <div style={{
                        position: "absolute", top: 2, left: data.lateFeePolicy ? 22 : 2,
                        width: 20, height: 20, borderRadius: "50%", background: "#fff",
                        transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }} />
                    </div>
                    <span style={{ fontSize: 12, color: data.lateFeePolicy ? "#163C2F" : "#9ca3af", fontWeight: 600 }}>
                      {data.lateFeePolicy ? "Included" : "Excluded"}
                    </span>
                  </label>
                </div>
                {data.lateFeePolicy && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e0dbd0", display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={radioStyle}>
                      {[
                        ["percentage", "Percentage (5% after 5-day grace period)"],
                        ["flat",       "Flat Rate ($ amount after 5-day grace period)"],
                      ].map(([val, lbl]) => (
                        <label key={val} style={radioBtn(data.lateFeeType === val)}>
                          <input type="radio" name="lateFeeType" value={val}
                            checked={data.lateFeeType === val}
                            onChange={() => set("lateFeeType", val)}
                            style={{ accentColor: "#163C2F" }} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                    {data.lateFeeType === "flat" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, color: "#9B9690" }}>$</span>
                        <input
                          type="number" min="0" value={data.lateFeeAmount}
                          onChange={e => set("lateFeeAmount", e.target.value)}
                          style={{ ...inputStyle, maxWidth: 140, marginBottom: 0 }}
                          placeholder="e.g. 75"
                        />
                        <span style={{ fontSize: 12, color: "#9B9690" }}>flat fee</span>
                      </div>
                    )}
                  </div>
                )}
                {data.lateFeePolicy && (
                  <ClausePreviewToggle
                    open={!!previewOpen.lateFee} onToggle={() => togglePreview("lateFee")}
                    text={getLateFeeText(data.lateFeePolicy, data.lateFeeType, data.lateFeeAmount)}
                  />
                )}
              </div>

              {[
                ["maintenancePolicy", "🔧 Maintenance Responsibilities", "Landlord handles major repairs; tenant handles minor fixes under $50", getMaintenanceText()],
                ["guestPolicy",       "🏠 Guest Policy",              "No guests staying more than 14 consecutive days", getGuestText()],
                ["noSmoking",         "🚭 No Smoking Clause",         "No smoking inside or within 25 ft of the building", getNoSmokingText()],
              ].map(([key, title, desc, previewText]) => (
                <div key={key} style={{ ...toggleRow, flexDirection: "column", alignItems: "stretch", gap: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
                      <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>{desc}</div>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <div style={{
                        width: 44, height: 24, borderRadius: 12, cursor: "pointer",
                        background: data[key] ? "#163C2F" : "#d1d5db",
                        position: "relative", transition: "background 0.2s",
                        flexShrink: 0,
                      }} onClick={() => set(key, !data[key])}>
                        <div style={{
                          position: "absolute", top: 2, left: data[key] ? 22 : 2,
                          width: 20, height: 20, borderRadius: "50%", background: "#fff",
                          transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        }} />
                      </div>
                      <span style={{ fontSize: 12, color: data[key] ? "#163C2F" : "#9ca3af", fontWeight: 600 }}>
                        {data[key] ? "Included" : "Excluded"}
                      </span>
                    </label>
                  </div>
                  {data[key] && (
                    <ClausePreviewToggle
                      open={!!previewOpen[key]} onToggle={() => togglePreview(key)}
                      text={previewText}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Step 5: Preview ── */}
          {step === 5 && (() => {
            const bodyStart = leaseText.indexOf("1. LEASE TERM");
            const leaseBody = bodyStart >= 0 ? leaseText.slice(bodyStart) : leaseText;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Mini header replica */}
                <div style={{ textAlign: "center", paddingBottom: 12, borderBottom: "2px solid #163C2F" }}>
                  <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 700, fontSize: 15, color: "#163C2F", letterSpacing: "0.1em", fontVariant: "small-caps" }}>
                    Gibbs Prodigy Group
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#163C2F", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 3 }}>
                    Residential Lease Agreement
                  </div>
                  <div style={{ fontSize: 11, color: "#9B9690", marginTop: 3 }}>
                    {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </div>
                </div>

                {/* Parties — two columns */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 12, lineHeight: 1.8 }}>
                  <div style={{ paddingRight: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#163C2F", borderBottom: "1px solid #163C2F", paddingBottom: 4, marginBottom: 8 }}>Landlord</div>
                    <div>Name: {resolveLandlordName(companyProfile, settings)}</div>
                    <div>Address: {data.propertyAddress || "[Property Address]"}</div>
                  </div>
                  <div style={{ paddingLeft: 20, borderLeft: "1px solid #bbb" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#163C2F", borderBottom: "1px solid #163C2F", paddingBottom: 4, marginBottom: 8 }}>Tenant</div>
                    <div>Name: {data.tenantName || "[Tenant Name]"}</div>
                    {data.tenantPhone && <div>Phone: {data.tenantPhone}</div>}
                    {data.tenantEmail && <div>Email: {data.tenantEmail}</div>}
                  </div>
                </div>

                <pre style={{
                  background: "#F5F1EA", border: "1px solid #e0dbd0", borderRadius: 10,
                  padding: "16px 18px", fontSize: 12, color: "#2d4a2d",
                  whiteSpace: "pre-wrap", fontFamily: "Georgia, 'Times New Roman', serif",
                  lineHeight: 1.75, maxHeight: 360, overflowY: "auto",
                }}>{leaseBody}</pre>

                <div style={{ padding: "10px 14px", background: "#F5F1EA", borderRadius: 8, fontSize: 10, color: "#9ca3af", textAlign: "center", fontFamily: "'Manrope', sans-serif", letterSpacing: "0.05em" }}>
                  Powered by Leasly &nbsp;·&nbsp; Gibbs Prodigy Group &nbsp;·&nbsp; Pittsburg, CA
                </div>
              </div>
            );
          })()}
        </div>

        {/* Footer nav */}
        <div style={{ borderTop: "1px solid #e0dbd0", background: "#FFFFFF" }}>
          {(leaseActionError || sendValidationErr) && (
            <div style={{ padding: "10px 28px 0" }}>
              <div style={{ background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626" }}>
                {leaseActionError || sendValidationErr}
              </div>
            </div>
          )}
          <div style={{ padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              {step > 1 && (
                <button onClick={handleBack} disabled={leaseActionPending || checkingConflict} style={btnSecondary}>← Back</button>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {step < STEPS.length ? (
                <button onClick={handleNext} disabled={!canNext() || checkingConflict}
                  style={{ ...btnPrimary, opacity: (canNext() && !checkingConflict) ? 1 : 0.4, cursor: (canNext() && !checkingConflict) ? "pointer" : "not-allowed" }}>
                  {checkingConflict ? "Generating…" : "Next →"}
                </button>
              ) : (
                <>
                  <button onClick={handlePrint} disabled={leaseActionPending} style={btnSecondary}>🖨️ Print / Save as PDF</button>
                  <button onClick={handleSaveDraft} disabled={leaseActionPending}
                    style={{ ...btnSecondary, opacity: leaseActionPending ? 0.6 : 1 }}>
                    💾 Save as Draft
                  </button>
                  <button onClick={handleSendForSignature} disabled={leaseActionPending}
                    style={{ ...btnPrimary, opacity: leaseActionPending ? 0.6 : 1 }}>
                    {leaseActionPending ? "Sending…" : "✉️ Send for E-Signature"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
